import { supabase } from './supabase';
import { CalendarBooking } from './bookings';

const formatDateTime = (date: string, time: string): string => {
  const dateStr = String(date);
  const timeStr = String(time);
  const timeWithSeconds = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  return `${dateStr}T${timeWithSeconds}`;
};

const getExclusiveEnd = (_start: string, end: string, allDay: boolean) => {
  if (!allDay) return end;
  const endDate = new Date(end);
  endDate.setDate(endDate.getDate() + 1);
  return endDate.toISOString().split('T')[0];
};

const getTaskStatusColor = (status: string, task: any): string => {
  if (status === 'scheduled' && task.color && task.color !== '#3b82f6') {
    return task.color;
  }

  if (status === 'in_progress') return '#eab308';

  switch (status) {
    case 'scheduled':
      return '#000000';
    case 'completed':
      return '#22c55e';
    default:
      return '#000000';
  }
};

export async function getCalendarTasks(
  businessId: string,
  startDate: Date,
  endDate: Date,
  filterByResourceId?: string
): Promise<CalendarBooking[]> {
  if (!supabase) throw new Error('Supabase client is not initialized');

  const formattedStartDate = startDate.toISOString().split('T')[0];
  const formattedEndDate = endDate.toISOString().split('T')[0];

  let query = supabase
    .from('calendar_tasks')
    .select(`
      *,
      customer:customers!calendar_tasks_customer_id_fkey(
        id, first_name, last_name, email, phone
      ),
      resources:calendar_task_resources(
        *,
        resource:resources(
          id, first_name, last_name, avatar_url
        )
      ),
      task_assets:calendar_task_assets(
        *,
        asset:assets(id, name, asset_type, image_url, status)
      )
    `)
    .eq('business_id', businessId)
    .eq('is_deleted', false)
    .lte('start_date', formattedEndDate)
    .gte('end_date', formattedStartDate)
    .order('start_date', { ascending: true });

  if (filterByResourceId) {
    query = supabase
      .from('calendar_tasks')
      .select(`
        *,
        customer:customers!calendar_tasks_customer_id_fkey(
          id, first_name, last_name, email, phone
        ),
        resources:calendar_task_resources!inner(
          *,
          resource:resources(
            id, first_name, last_name, avatar_url
          )
        ),
        task_assets:calendar_task_assets(
          *,
          asset:assets(id, name, asset_type, image_url, status)
        )
      `)
      .eq('business_id', businessId)
      .eq('is_deleted', false)
      .eq('resources.resource_id', filterByResourceId)
      .lte('start_date', formattedEndDate)
      .gte('end_date', formattedStartDate)
      .order('start_date', { ascending: true });
  }

  const { data: tasks, error } = await query;

  if (error) {
    console.error('Error fetching calendar tasks:', error);
    throw error;
  }

  if (!tasks) return [];

  return tasks.map((task: any) => {
    const start = formatDateTime(task.start_date, task.start_time);
    let end = task.end_date && task.end_time
      ? formatDateTime(task.end_date, task.end_time)
      : formatDateTime(task.start_date, task.start_time);

    let allDay = false;
    if (task.start_date !== task.end_date) {
      const startTime = task.start_time;
      const endTime = task.end_time;
      if ((startTime === '00:00' || startTime === '00:00:00') && (endTime === '00:00' || endTime === '00:00:00')) {
        allDay = true;
        end = getExclusiveEnd(start, end, true);
      }
    }

    const color = getTaskStatusColor(task.status, task);

    const resources = task.resources?.map((tr: any) => ({
      id: tr.resource.id,
      first_name: tr.resource.first_name,
      last_name: tr.resource.last_name,
      avatar_url: tr.resource.avatar_url
    })) || [];

    const taskAssets = task.task_assets?.map((ta: any) => ({
      id: ta.asset_id,
      asset_id: ta.asset_id,
      name: ta.asset?.name || 'Asset',
      asset_type: ta.asset?.asset_type || null,
      image_url: ta.asset?.image_url || null,
    })) || [];

    const resourceId = resources.length > 0
      ? resources[0].id
      : (taskAssets.length > 0 ? taskAssets[0].id : null);

    return {
      id: task.id,
      title: task.title || `Event #${task.task_number}`,
      booking_number: 0,
      start,
      end,
      backgroundColor: color,
      borderColor: color,
      secondaryColor: task.secondary_color || null,
      allDay,
      display: 'block',
      resourceId,
      extendedProps: {
        customer: task.customer
          ? `${task.customer.first_name} ${task.customer.last_name}`
          : '',
        customer_phone: task.customer?.phone || null,
        customer_email: task.customer?.email || null,
        customer_id: task.customer?.id || null,
        status: task.status,
        booking_number: 0,
        subtotal: 0,
        total_amount: 0,
        discount_amount: 0,
        start_date: task.start_date || null,
        start_time: task.start_time || null,
        end_date: task.end_date || null,
        end_time: task.end_time || null,
        drop_off_date: null,
        drop_off_time: null,
        payments: [],
        services: [],
        resources,
        booking_assets: taskAssets,
        is_buffer_block: false,
        mobile_booking: task.is_mobile || false,
        resource_id: resourceId,
        customer_notes: undefined,
        secondaryColor: task.secondary_color || null,
        booking_address: task.task_address || null,
        booking_workflows: [],
        address: task.task_address || task.customer?.address || null,
        lat: task.task_lat || task.customer?.lat || null,
        lng: task.task_lng || task.customer?.lng || null,
        is_task: true,
      }
    } as CalendarBooking;
  });
}
